import React, { useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import { ModalScopeContext } from "../context/ModalScopeContext";

const SIZE_CLASS = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-3xl",
  "2xl": "max-w-6xl",
  preview: "max-w-[460px]",
  full: "max-w-4xl",
};

const AppModal = ({
  open,
  onClose,
  children,
  title,
  footer,
  size = "md",
  className = "",
  panelClassName = "",
  bodyClassName = "",
  zIndex = 50,
  closeOnBackdrop = true,
  showClose = true,
}) => {
  const scopeRef = useContext(ModalScopeContext);
  const scopeEl = scopeRef?.current;
  const isScoped = Boolean(scopeEl);

  useEffect(() => {
    if (!open || !isScoped || !scopeEl) return undefined;
    const previousOverflow = scopeEl.style.overflow;
    scopeEl.style.overflow = "hidden";
    return () => {
      scopeEl.style.overflow = previousOverflow;
    };
  }, [open, isScoped, scopeEl]);

  if (!open) return null;

  const overlayClass = isScoped
    ? "absolute inset-0 flex items-center justify-center app-modal-overlay p-4"
    : "fixed inset-0 flex items-center justify-center app-modal-overlay p-4";

  const modal = (
    <div
      className={overlayClass}
      style={{ zIndex }}
      onClick={closeOnBackdrop && onClose ? onClose : undefined}
      role="presentation"
    >
      <div
        className={`app-modal-panel w-full ${SIZE_CLASS[size] || SIZE_CLASS.md} ${className} ${panelClassName}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : "Dialog"}
      >
        {(title || (showClose && onClose)) && (
          <div className="app-modal-header">
            {title ? <h3 className="app-modal-title">{title}</h3> : <span />}
            {showClose && onClose && (
              <button type="button" onClick={onClose} className="app-modal-close" aria-label="Close">
                ×
              </button>
            )}
          </div>
        )}
        <div className={`app-modal-body ${title || (showClose && onClose) ? "" : "!pt-0"} ${bodyClassName}`}>{children}</div>
        {footer ? <div className="app-modal-footer">{footer}</div> : null}
      </div>
    </div>
  );

  return createPortal(modal, isScoped ? scopeEl : document.body);
};

export default AppModal;
